import { useEffect, useState } from "react";
import HttpService from "../services/HttpService";
import {
  Divider,
  Box,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { GridColDef } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import DialogAction from "../components/Dialog/DialogAction";
import Theme from "../Themes/main.theme";
import TableGridWithFilters from "../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters";
import {
  Auth_Method,
  User_Units_Status,
  User_Units_Status_Out,
} from "../lib/static/users/selectOptions";
import EnableSelector from "../components/EnableComponent/EnableSelector";
import LinkButton from "../components/Buttons/LinkButton";
import { FieldControllerTypes } from "../components/form_v2/types";
import TabsLayout from "../components/Tabs/TabsLayout";
import DialogForm from "../components/Dialog/DialogForm";
import { batchEmailsColumns } from "../lib/static/crm/filterFields";
import { batchEmailsInitialValues } from "../lib/static/crm/initialValues";
import { batchEmailsValidationSchema } from "../lib/static/crm/validationSchema";
import UserService from "../services/UserService";
import {
  PermissionsManageExternalUsers,
  PermissionsManageInternalUsers,
} from "../lib/permissions";
import { useUserDbInfo } from "../hooks/UserInfo";
import { Delete } from "@mui/icons-material";

const initialValues = {
  last_name: "",
  administrative_unit_id: "",
  role_id: "",
  is_enabled: "",
};

const initialValuesOut = {
  full_name: "",
  auth: "",
  is_enabled: "",
};

function UsersDashboard() {
  document.title = "Διαχείριση Χρηστών";

  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEmailForm, setOpenEmailForm] = useState(false);
  const [text, setText] = useState("");
  const [status, setStatus] = useState({ id: null, is_enabled: false });
  const [administrativeUnits, setAdministrativeUnits] = useState<
    { key: string; value: number }[]
  >([]);
  const [roles, setRoles] = useState<{ key: string; value: number }[]>();
  const [categories, setCategories] =
    useState<{ key: string; value: number }[]>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [reFetch, setReFetch] = useState(false);
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [emailCategories, setEmailCategories] = useState([]);

  const { userDbInfo } = useUserDbInfo();

  const fetchEmailCategories = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/email/categories`
      );
      setEmailCategories(response.data.categories);
    } catch (error) {
      console.error(error);
    }
  };

  const onOpenBatchEmail = async () => {
    try {
      fetchEmailCategories();
      setOpenEmailForm(true);
    } catch (error) {
      console.error(error);
    }
  };

  const submitEmails = async (values: any) => {
    try {
      await HttpService.getAxiosClient().post(
        `${process.env.REACT_APP_BACKEND_URL}/crm/emails`,
        values
      );
      enqueueSnackbar("Επιτυχής Αποστολή των emails", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.",
        { variant: "error" }
      );
    } finally {
      setOpenEmailForm(false);
    }
  };

  const filterFields = [
    [
      {
        control: FieldControllerTypes.input,
        type: "search",
        customLabel: true,
        name: "last_name",
        label: "Επώνυμο",
        filtersDesign: true,
      },
      {
        control: FieldControllerTypes.autocomplete,
        type: "autocomplete",
        label: "Αρμόδια Οργανική Μονάδα",
        name: "administrative_unit_id",
        filtersDesign: true,
        options: administrativeUnits,
      },
      {
        control: FieldControllerTypes.autocomplete,
        type: "autocomplete",
        label: "Ρόλος",
        name: "role_id",
        filtersDesign: true,
        options: roles,
      },
      {
        control: FieldControllerTypes.select,
        type: "autocomplete",
        label: "Κατάσταση",
        name: "is_enabled",
        filtersDesign: true,
        customLabel: true,
        options: User_Units_Status,
      },
    ],
  ];

  const columns: GridColDef[] = [
    {
      field: "full_name",
      headerName: "Ονοματεπώνυμο",
      minWidth: 300,
    },
    {
      field: "role",
      headerName: "Ρόλος",
      minWidth: 300,
      renderCell: ({ row }) => row.role || "-",
    },
    {
      field: "administrative_unit",
      headerName: "Αρμόδια Οργανική Μονάδα",
      minWidth: 280,
      renderCell: ({ row }) => row.administrative_unit || "-",
    },
    // {
    //   field: 'last_updated',
    //   headerName: 'Τελευταία Σύνδεση',
    //   minWidth: 184,
    //   renderCell: ({row}) => row.last_updated
    //     ? moment(row.last_updated).format('DD/MM/YYYY, hh:mm:ss').toLocaleString()
    //     : null
    // },
    {
      field: "is_enabled",
      headerName: "Κατάσταση",
      minWidth: 170,
      renderCell: (params) => (
        <EnableSelector
          IsEnabled={params.row.is_enabled}
          truthfulLabel={"Ενεργός"}
          falseLabel={"Ανενεργός"}
        />
      ),
    },
    {
      field: "action",
      headerName: "Ενέργειες",
      minWidth: 156,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip
            placement='right-end'
            PopperProps={{
              disablePortal: true,
            }}
            title='Προβολή Χρήστη'
          >
            <IconButton
              color='primary'
              onClick={() => navigate(`/admin/users/${params.row.id}`)}
            >
              <PageviewOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            placement='right-end'
            PopperProps={{
              disablePortal: true,
            }}
            title='Επεξεργασία Χρήστη'
          >
            <IconButton
              disabled={
                !(
                  UserService.hasRole([
                    PermissionsManageInternalUsers.PermissionsInternalUsersManageCreateAll,
                  ]) ||
                  (UserService.hasRole([
                    PermissionsManageInternalUsers.PermissionsInternalUsersManageCreateMyOrg,
                  ]) &&
                    userDbInfo?.administrative_unit_id ===
                      params.row.administrative_unit_id)
                )
              }
              color='primary'
              aria-label='edit'
              size='large'
              onClick={() => navigate(`/admin/users/edit/${params.row.id}`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            placement='right-end'
            PopperProps={{
              disablePortal: true,
            }}
            title='Αλλαγή Κατάστασης'
          >
            <IconButton
              color='primary'
              disabled={
                !(
                  UserService.hasRole([
                    PermissionsManageInternalUsers.PermissionsInternalUsersManageCreateAll,
                  ]) ||
                  (UserService.hasRole([
                    PermissionsManageInternalUsers.PermissionsInternalUsersManageCreateMyOrg,
                  ]) &&
                    userDbInfo?.administrative_unit_id ===
                      params.row.administrative_unit_id)
                )
              }
              onClick={() => {
                setOpen(true);
                setStatus({
                  id: params.row.id,
                  is_enabled: !params.row.is_enabled,
                });
              }}
            >
              {!params.row.is_enabled
                ? <PersonIcon />
                : <PersonOutlinedIcon />}
            </IconButton>
          </Tooltip>
          {UserService.hasRole([
            PermissionsManageInternalUsers.PermissionInternalUsersDelete,
          ]) && (
            <Tooltip
              placement='right-end'
              PopperProps={{
                disablePortal: true,
              }}
              title='Διαγραφή Χρήστη'
            >
              <IconButton
                color='primary'
                disabled={!params.row.can_be_deleted}
                onClick={() => {
                  setOpenDelete(true);
                  setStatus({
                    id: params.row.id,
                    is_enabled: !params.row.is_enabled,
                  });
                }}
              >
                <Delete
                  color={params.row.can_be_deleted
                    ? "error"
                    : "disabled"}
                />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const filterFieldsOut = [
    [
      {
        control: FieldControllerTypes.input,
        type: "search",
        customLabel: true,
        name: "last_name",
        label: "Επώνυμο / Επωνυμία",
        filtersDesign: true,
      },
      {
        control: FieldControllerTypes.autocomplete,
        type: "autocomplete",
        label: "Μέθοδος Πιστοποίησης",
        name: "auth",
        filtersDesign: true,
        options: Auth_Method,
      },
    ],
    [
      {
        control: FieldControllerTypes.select,
        type: "autocomplete",
        label: "Κατάσταση",
        name: "is_enabled",
        filtersDesign: true,
        options: User_Units_Status_Out,
        customLabel: true,
      },
      {
        control: FieldControllerTypes.autocomplete,
        type: "autocomplete",
        label: "Κατηγορία",
        name: "category_id",
        filtersDesign: true,
        options: categories,
      },
    ],
  ];

  const columnsOut: GridColDef[] = [
    {
      field: "full_name",
      headerName: "Ονοματεπώνυμο/Επωνυμία",
      minWidth: 300,
    },
    {
      field: "auth_method",
      headerName: "Μέθοδος Πιστοποίησης",
      minWidth: 260,
    },
    {
      field: "category",
      headerName: "Κατηγορία",
      minWidth: 300,
      sortable: false,
    },
    {
      field: "is_enabled",
      headerName: "Κατάσταση",
      minWidth: 190,
      renderCell: (params) => (
        <EnableSelector
          IsEnabled={params.row.is_enabled}
          truthfulLabel={"Ενεργός"}
          falseLabel={"Αποκλεισμένος"}
        />
      ),
    },
    {
      field: "action",
      headerName: "Ενέργειες",
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip
            PopperProps={{
              disablePortal: true,
            }}
            title='Προβολή Χρήστη'
          >
            <IconButton
              color='primary'
              disabled={
                !UserService.hasRole([
                  PermissionsManageExternalUsers.PermissionInternalManageExternalUserBasicInfoRead,
                ])
              }
              onClick={() => navigate(`/admin/visitor/${params.row.id}`)}
            >
              <PageviewOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            PopperProps={{
              disablePortal: true,
            }}
            title='Αλλαγή Κατάστασης'
          >
            <IconButton
              color='primary'
              disabled={
                !UserService.hasRole([
                  PermissionsManageExternalUsers.PermissionInternalManageExternalUserBlockUnblock,
                ])
              }
              onClick={() => {
                setOpen(true);
                setStatus({
                  id: params.row.id,
                  is_enabled: !params.row.is_enabled,
                });
              }}
            >
              {!params.row.is_enabled
                ? (
                  <PersonOffIcon />
                )
                : (
                  <PersonOffOutlinedIcon />
                )}
            </IconButton>
          </Tooltip>
          {/* {UserService.hasRole([
            PermissionsManageInternalUsers.PermissionInternalUsersDelete,
          ]) && (
            <Tooltip
              placement='right-end'
              PopperProps={{
                disablePortal: true,
              }}
              title='Διαγραφή Χρήστη'
            >
              <IconButton
                color='primary'
                disabled={!params.row.can_be_deleted}
                onClick={() => {
                  setOpenDelete(true);
                  setStatus({
                    id: params.row.id,
                    is_enabled: !params.row.is_enabled,
                  });
                }}
              >
                <Delete
                  color={params.row.can_be_deleted
                    ? "error"
                    : "disabled"}
                />
              </IconButton>
            </Tooltip>
          )} */}
        </>
      ),
    },
  ];

  const onSubmit = async () => {
    try {
      const resp = await HttpService.getAxiosClient().patch(
        `${process.env.REACT_APP_BACKEND_URL}/users/${status.id}`,
        {
          is_enabled: status.is_enabled,
          disable_reason: text,
        }
      );
      if (resp.status === 200) {
        enqueueSnackbar(
          `Επιτυχής αλλαγή κατάστασης του ${resp.data.first_name} ${resp.data.last_name}`,
          { variant: "success" }
        );
      }
      setReFetch(!reFetch);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.",
        { variant: "error" }
      );
    }
  };

  const getAdminUnits = async () => {
    const response = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/administrative_unit`
    );
    setAdministrativeUnits(
      response.data.units.sort((a: any, b: any) =>
        a.key.localeCompare(b.key, "el")
      )
    );
  };

  const getRoles = async () => {
    const responseRoles = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/roles`
    );
    setRoles(responseRoles.data.roles.sort());
  };

  const getCategories = async () => {
    const responseRoles = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/categories`
    );
    setCategories(
      responseRoles.data.categories.sort((a: any, b: any) =>
        a.key.localeCompare(b.key, "el")
      )
    );
  };

  const onRowsChosen = (rows: any) => {
    setFetchedUsers(rows.map((row: any) => JSON.parse(row).email));
  };

  const deleteUser = async (id: number | null) => {
    try {
      await HttpService.getAxiosClient().delete(
        `${process.env.REACT_APP_BACKEND_URL}/users/${id}`
      );
      enqueueSnackbar("Ο Χρήστης Διαγράφηκε επιτυχώς", { variant: "success" });
      setReFetch(!reFetch);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Παρουσιάστηκε πρόβλημα με την διαγραφή του χρήστη! Προσπαθήστε ξανά αργότερα! ",
        { variant: "error" }
      );
    }
  };

  const tabLabels = [
    ...(UserService.hasRole([
      PermissionsManageInternalUsers.PermissionInternalUsersManageReadAll,
      PermissionsManageInternalUsers.PermissionInternalUsersManageReadMyOrg,
    ])
      ? [
        {
          label: <Typography variant='h4'>Εσωτερικοί Χρήστες</Typography>,
        },
      ]
      : []),

    ...(UserService.hasRole([
      PermissionsManageExternalUsers.PermissionInternalManageExternalUserListRead,
    ])
      ? [
        {
          label: <Typography variant='h4'>Εξωτερικοί Χρήστες</Typography>,
        },
      ]
      : []),
  ];

  const tab1 = (
    <Box>
      <Box display='flex' justifyContent='end'
        mt={5}>
        {UserService.hasRole([
          PermissionsManageInternalUsers.PermissionsInternalUsersManageCreateAll,
          PermissionsManageInternalUsers.PermissionsInternalUsersManageCreateMyOrg,
        ]) && (
          <LinkButton
            link='/admin/users/new'
            color='primary'
            variant='contained'
            sx={{
              borderRadius: 24,
              maxWidth: "300px",
              maxHeight: "55px",
              minWidth: "250px",
              minHeight: "55px",
            }}
          >
            Δημιουργία Χρήστη
          </LinkButton>
        )}
      </Box>
      <TableGridWithFilters
        reFetch={reFetch}
        pageSize={15}
        rowsPerPageOptions={[15, 30, 45]}
        sortingMode='server'
        paginationMode='server'
        responseDataFieldName='data'
        responseTotalCountFieldName='total_count'
        initialValues={initialValues}
        filterFields={filterFields}
        columns={columns}
        fetchUrl={
          UserService.hasRole([
            PermissionsManageInternalUsers.PermissionInternalUsersManageReadAll,
          ])
            ? `${process.env.REACT_APP_BACKEND_URL}/users?role=internal`
            : `${process.env.REACT_APP_BACKEND_URL}/users/my-org?role=internal`
        }
        resetFilters={true}
        resetFiltersTo={initialValues}
      />
      <DialogAction
        open={open}
        title={"Αλλαγή Κατάστασης"}
        handleClose={() => {
          setOpen(false);
        }}
        content='Είστε σίγουρος/η ότι θέλετε να αλλάξετε την κατάσταση;'
        onAccept={() => {
          onSubmit();
          setOpen(false);
        }}
      />
      <DialogAction
        open={openDelete}
        title='Οριστική Διαγραφή Χρήστη'
        mode='delete'
        handleClose={() => {
          setOpenDelete(false);
        }}
        content='Είστε σίγουρος/η ότι θέλετε να διαγράψετε τον χρήστη;'
        onAccept={() => {
          deleteUser(status.id);
          setOpenDelete(false);
        }}
      />
    </Box>
  );

  const tab2 = (
    <Box>
      <TableGridWithFilters
        componentInBetween={
          fetchedUsers.length > 0
            ? (
              <Box display='flex' justifyContent='end'
                mt={2}>
                <Button variant='contained' onClick={onOpenBatchEmail}>
                  Μαζική Αποστολή Email
                </Button>
              </Box>
            )
            : (
              <></>
            )
        }
        pageSize={15}
        reFetch={reFetch}
        rowsPerPageOptions={[15, 30, 45]}
        responseDataFieldName='data'
        responseTotalCountFieldName='total_count'
        sortingMode='server'
        paginationMode='server'
        initialValues={initialValuesOut}
        filterFields={filterFieldsOut}
        columns={columnsOut}
        fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/users?role=external`}
        resetFilters={true}
        onRowsChosen={onRowsChosen}
        checkboxSelection
      />
      <DialogAction
        fullWidth
        open={open}
        title={
          status.is_enabled
            ? "Ενεργοποίηση Εξωτερικού Χρήστη"
            : "Αποκλεισμός Εξωτερικού Χρήστη"
        }
        handleClose={() => {
          setOpen(false);
        }}
        content={
          status.is_enabled
            ? (
              "Είστε σίγουρος/η ότι θέλετε να ενεργοποιήσετε τον χρήστη;"
            )
            : (
              <TextField
                value={text}
                onChange={(e: any) => setText(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                placeholder={"Εισάγετε αιτιολογία αποκλεισμού"}
              />
            )
        }
        onAccept={() => {
          onSubmit();
          setOpen(false);
        }}
        disableAcceptButton={status.is_enabled
          ? false
          : !text}
      />
      <DialogForm
        open={openEmailForm}
        title='Emails'
        handleClose={() => setOpenEmailForm(false)}
        fields={batchEmailsColumns(emailCategories)}
        initialValues={batchEmailsInitialValues(fetchedUsers)}
        validationSchema={batchEmailsValidationSchema}
        onAccept={submitEmails}
      />
      <DialogAction
        open={openDelete}
        title='Οριστική Διαγραφή Χρήστη'
        mode='delete'
        handleClose={() => {
          setOpenDelete(false);
        }}
        content='Είστε σίγουρος/η ότι θέλετε να διαγράψετε τον χρήστη;'
        onAccept={() => {
          deleteUser(status.id);
          setOpenDelete(false);
        }}
      />
    </Box>
  );

  const tabContent = [
    ...(UserService.hasRole([
      PermissionsManageInternalUsers.PermissionInternalUsersManageReadAll,
      PermissionsManageInternalUsers.PermissionInternalUsersManageReadMyOrg,
    ])
      ? [tab1]
      : []),

    ...(UserService.hasRole([
      PermissionsManageExternalUsers.PermissionInternalManageExternalUserListRead,
    ])
      ? [tab2]
      : []),
  ];

  useEffect(() => {
    getAdminUnits();
    getCategories();
    getRoles();
  }, []);

  return (
    <>
      <Typography
        sx={{ my: 2, mt: 4 }}
        variant='h2'
        color={Theme?.labelColor?.main}
      >
        Διαχείριση Χρηστών Συστήματος
      </Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
      />
    </>
  );
}

export default UsersDashboard;
